"use client";

import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const services = [{
  numeral: "01",
  label: "CONSULTING",
  title: "Strategic Business Consulting",
  description: "We provide high-level strategic guidance to help your business navigate complex market landscapes. Our approach focuses on identifying growth opportunities, optimizing operational efficiency, and ensuring your long-term vision is supported by actionable, data-driven insights.",
  items: ["Market Analysis", "Growth Strategy", "Operational Audit", "Risk Assessment", "Change Management"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1641356/img/business_consulting.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1641356/img/business_consulting.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1641356/img/business_consulting.webp-wvc-sizes",
  alt: "Strategic business consulting session",
  reverse: false
}, {
  numeral: "02",
  label: "MANAGEMENT",
  title: "Project Management",
  description: "Execution is the bridge between strategy and results. We manage your most critical projects with precision, ensuring timelines are met, resources are allocated effectively, and stakeholders remain aligned throughout the entire lifecycle of the initiative.",
  items: ["Resource Planning", "Timeline Tracking", "Stakeholder Alignment", "Risk Mitigation", "Quality Assurance"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1641356/img/project_management.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1641356/img/project_management.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1641356/img/project_management.webp-wvc-sizes",
  alt: "Professional project management workflow",
  reverse: true
}, {
  numeral: "03",
  label: "SUPPORT",
  title: "Customized Support",
  description: "Every organization faces unique challenges that require tailored solutions. Our support services are designed to integrate seamlessly with your existing teams, providing the specialized expertise needed to overcome hurdles and drive sustainable performance.",
  items: ["Team Augmentation", "Process Optimization", "Technical Support", "Performance Coaching", "Bespoke Solutions"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1641356/img/customized_support.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1641356/img/customized_support.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1641356/img/customized_support.png-wvc-sizes",
  alt: "Customized professional support services",
  reverse: false
}];
const indexItems = [{
  roman: "I.",
  label: "STRATEGIC CONSULTING"
}, {
  roman: "II.",
  label: "PROJECT MANAGEMENT"
}, {
  roman: "III.",
  label: "CUSTOMIZED SUPPORT"
}, {
  roman: "IV.",
  label: "PERFORMANCE OPTIMIZATION"
}];
function ServiceBlock({
  service,
  index
}: {
  service: (typeof services)[number];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div ref={ref} data-index={index} className={`transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center">
        {}
        <div className={`lg:col-span-6 ${service.reverse ? "lg:order-2" : "lg:order-1"}`}>
          <div className="relative overflow-hidden aspect-[5/6]">
            <img src={service.image} data-wvc-srcset={service.srcset} data-wvc-sizes={service.sizes} alt={service.alt} loading="lazy" decoding="async" className="w-full h-full object-cover" />
          </div>
        </div>

        {}
        <div className={`lg:col-span-6 relative ${service.reverse ? "lg:order-1" : "lg:order-2"}`}>
          {}
          <div aria-hidden="true" className="font-default text-[120px] leading-none font-light text-foreground/10 select-none pointer-events-none -mb-8 lg:-mb-12" style={{
          fontFamily: "var(--font-serif)"
        }}>
            {service.numeral}
          </div>

          <div className="relative">
            <div className="text-xs uppercase tracking-[0.2em] mb-6" style={{
            fontFamily: "var(--font-mono)",
            color: "var(--primary)"
          }}>
              — {service.label}
            </div>

            <h3 className="font-default text-3xl md:text-4xl lg:text-[44px] leading-[1.05] font-light text-foreground mb-8" style={{
            fontFamily: "var(--font-serif)"
          }}>
              {service.title}
            </h3>

            <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-8 max-w-xl">
              {service.description}
            </p>

            <ul className="flex flex-wrap gap-x-6 gap-y-2 mb-10">
              {service.items.map((item, i) => <li key={i} data-index={i} className="text-xs uppercase tracking-[0.15em] text-muted-foreground" style={{
              fontFamily: "var(--font-mono)"
            }}>
                  {item}
                </li>)}
            </ul>

            <Button variant="outline" className="border-border bg-transparent text-foreground hover:bg-primary hover:text-primary-foreground h-12 px-6 text-xs uppercase tracking-[0.2em]" style={{
            fontFamily: "var(--font-mono)"
          }} asChild>
              <Link to="/ConnectPage">
                Discuss this service
                <ArrowRight className="ml-2 size-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </div>;
}
export default function Expertise() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="3650" id="our-expertise" className="bg-background text-foreground pt-[120px] md:pt-[160px] lg:pt-[200px] pb-24 lg:pb-32 px-6 md:px-10 lg:px-16">
      <div className="max-w-[1440px] mx-auto">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {}
          <div className="lg:col-start-2 lg:col-span-6">
            <div className="text-xs uppercase tracking-[0.25em] mb-12" style={{
            fontFamily: "var(--font-mono)",
            color: "var(--primary)"
          }}>
              — SERVICES / 01
            </div>

            <h2 className="font-default text-4xl md:text-6xl lg:text-[88px] leading-[1.02] font-light text-foreground mb-8" style={{
            fontFamily: "var(--font-serif)"
          }}>
              Professional expertise,
              <br />
              from <em className="italic font-light">strategy</em> to{" "}
              <em className="italic font-light">success</em>.
            </h2>

            <p className="text-base md:text-lg text-muted-foreground leading-relaxed max-w-[580px] mt-8">
              ITLS provides comprehensive professional services designed to empower discerning clients. 
              From strategic consulting and project management to customized operational support, 
              we prioritize transparency, innovation, and measurable results to ensure your long-term success.
            </p>
          </div>

          {}
          <div className="lg:col-start-9 lg:col-span-4">
            <div className="lg:pt-16">
              <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-6" style={{
              fontFamily: "var(--font-mono)"
            }}>
                — INDEX
              </div>
              <ul className="border-t border-border">
                {indexItems.map((item, i) => <li key={i} data-index={i} className="flex items-baseline justify-between gap-4 py-4 border-b border-border">
                    <span className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground" style={{
                  fontFamily: "var(--font-mono)"
                }}>
                      {item.roman}
                    </span>
                    <span className="text-[11px] uppercase tracking-[0.2em] text-foreground text-right" style={{
                  fontFamily: "var(--font-mono)"
                }}>
                      {item.label}
                    </span>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>

        {}
        <div className="mt-[80px] lg:mt-[120px] space-y-24 lg:space-y-32">
          {services.map((service, i) => <div key={i} data-index={i}>
              <ServiceBlock service={service} index={i} />
              {i < services.length - 1 && <div className="relative mt-24 lg:mt-32 flex items-center">
                  <div className="flex-1 h-px bg-border" />
                  <div className="px-6 text-xs uppercase tracking-[0.25em]" style={{
              fontFamily: "var(--font-mono)",
              color: "var(--primary)"
            }}>
                    / 0{i + 2}
                  </div>
                  <div className="flex-1 h-px bg-border" />
                </div>}
            </div>)}
        </div>
      </div>
    </section>;
}